import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { OrganisationTypeConstants } from "../../constants";
import { searchPipelineDeals } from "../../service/query";
import { Status } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { getDealListingRoute, getNewDealForObjectRoute } from "../routes";

export interface DealQuickActionObject {
  uuid: string;
  /** The deal association type, e.g. "Project" or "Landholding". */
  type: string;
  name?: string;
}

export interface DealQuickAction {
  value: string;
  action: () => void;
}

/**
 * The Seed quick action for an object a deal can be raised against: "View deal" when a deal is associated with
 * the object, otherwise "Create new deal" (opening the new deal journey with the object pre-selected).
 *
 * Returns undefined while the lookup is in flight, so the menu never shows one option and then swaps to the other.
 * Pass an undefined object to skip the lookup, e.g. for users who are not asset managers.
 */
export const useDealQuickAction = (
  object: DealQuickActionObject | undefined,
  onError?: () => void
): DealQuickAction | undefined => {
  const navigate = useNavigate();
  const { currentOrganisationUuid } = useAuth();
  // undefined while the lookup is in flight, null when no deal exists against the object.
  const [existingDealUuid, setExistingDealUuid] = useState<string | null>();

  const objectUuid = object?.uuid;

  useEffect(() => {
    setExistingDealUuid(undefined);
    if (!objectUuid || !currentOrganisationUuid) return undefined;

    // Ignores a response that lands after the object changed or the page unmounted.
    let isCurrent = true;

    searchPipelineDeals({
      organisationUuid: currentOrganisationUuid,
      paging: { afterCursor: null, beforeCursor: null, limit: 1 },
      filter: {
        results: {
          relatedObjects: { some: { objectUuid: { operator: "eq", value: objectUuid } } },
        },
      },
      sort: [{ key: "results.createdAt", direction: "desc" }],
    })
      .then((response) => {
        if (!isCurrent) return;
        if (response.status !== Status.Success) {
          onError?.();
          return;
        }
        setExistingDealUuid(response.data?.results[0]?.uuid ?? null);
      })
      .catch(() => {
        if (isCurrent) onError?.();
      });

    return () => {
      isCurrent = false;
    };
  }, [objectUuid, currentOrganisationUuid]);

  if (!object) return undefined;

  if (existingDealUuid) {
    return {
      value: "View deal",
      action: () => navigate(getDealListingRoute(existingDealUuid, OrganisationTypeConstants.ASSET_MANAGER)),
    };
  }

  if (existingDealUuid === null) {
    return {
      value: "Create new deal",
      action: () => navigate(getNewDealForObjectRoute(object.uuid, object.type, object.name)),
    };
  }

  return undefined;
};
